import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { wingCalKeys } from "../query-keys";
import type { BuildId, EventId } from "@/types/identifiers";
import type { components } from "../generated/schema";

export { wingCalKeys } from "../query-keys";

export type WingCalTeamStatus = components["schemas"]["WingCalTeamStatus"];
export type WingCompareResult = components["schemas"]["BoatCompareResponse"];
export type WingCalHistory = components["schemas"]["WingCalHistory"];
export type WingCalHistoryEntry = components["schemas"]["WingCalHistoryEntry"];
export type WingCalState = components["schemas"]["WingCalibrationState"];

export function useWingCalHistory(buildId: BuildId) {
  return useQuery({
    queryKey: wingCalKeys.history(buildId),
    queryFn: async () => {
      const result = await api.GET("/api/wing-cal-sync/builds/{build_id}/history", {
        params: { path: { build_id: buildId } },
      });
      return throwIfError(result, "Failed to fetch wing cal history");
    },
    enabled: !!buildId,
  });
}

export function useWingCalStatuses(eventId: EventId) {
  return useQuery({
    queryKey: wingCalKeys.byEvent(eventId),
    queryFn: async () => {
      const result = await api.GET("/api/wing-cal-sync/events/{event_id}", {
        params: { path: { event_id: eventId } },
      });
      return throwIfError(result, "Failed to fetch wing cal statuses");
    },
    enabled: !!eventId,
  });
}

export async function compareWingGolden(buildId: BuildId) {
  const result = await api.POST("/api/wing-cal-sync/builds/{build_id}/compare", {
    params: { path: { build_id: buildId } },
  });
  return throwIfError(result, "Failed to compare wing cals");
}

export async function restoreWingGolden(buildId: BuildId) {
  const result = await api.POST("/api/wing-cal-sync/builds/{build_id}/restore", {
    params: { path: { build_id: buildId } },
  });
  return throwIfError(result, "Failed to restore wing cals");
}

/** Read the live wing NVP onto this build. Lands as an as-found `initial`
 * unless `promote`, which stores it straight as the golden. See ADR-050. */
export async function captureWingCal(buildId: BuildId, promote = false) {
  const result = await api.POST("/api/wing-cal-sync/builds/{build_id}/accept", {
    params: { path: { build_id: buildId }, query: { promote } },
  });
  return throwIfError(
    result,
    promote ? "Failed to accept wing cals as golden" : "Failed to capture wing cals",
  );
}

export async function setWingCalState(
  buildId: BuildId,
  calId: string,
  state: WingCalState,
) {
  const result = await api.POST(
    "/api/wing-cal-sync/builds/{build_id}/cals/{cal_id}/state",
    { params: { path: { build_id: buildId, cal_id: calId } }, body: { state } },
  );
  return throwIfError(result, "Failed to change wing cal state");
}

/** Capture the live wing NVP onto this build. Rejected by the backend unless
 * EVERY coef reads back (see ADR-048). */
export function useCaptureWingCal(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => captureWingCal(buildId),
    // The whole key space: a capture also changes the event-level status list.
    onSuccess: () => qc.invalidateQueries({ queryKey: wingCalKeys.all }),
  });
}

export function useSetWingCalState(buildId: BuildId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calId, state }: { calId: string; state: WingCalState }) =>
      setWingCalState(buildId, calId, state),
    // Promoting moves the effective golden, so the event list moves with it.
    onSuccess: () => qc.invalidateQueries({ queryKey: wingCalKeys.all }),
  });
}
